import { Injectable, effect } from '@angular/core';
import { LoggerService } from '@core/services/infra/logger.service';
import { BehaviorSubject, take } from 'rxjs';
import { UserService } from '@core/services/auth/user.service';
import { AuthStore } from '@core/services/auth/auth.store';

export type DateFormatType = 'belgian' | 'us' | 'iso' | 'european';

type DateComponentOrder = 'dmy' | 'mdy' | 'ymd';

export interface DateFormatConfig {
  short: string;
  medium: string;
  long: string;
  locale: string;
  separator: string;
  intlOptions: {
    short: Intl.DateTimeFormatOptions;
    medium: Intl.DateTimeFormatOptions;
    long: Intl.DateTimeFormatOptions;
  };
}

@Injectable({
  providedIn: 'root'
})
export class DateFormatService {
  private readonly CONTEXT = 'DateFormatService';

  private currentFormatSubject = new BehaviorSubject<DateFormatType>('belgian');
  public currentFormat$ = this.currentFormatSubject.asObservable();

  private readonly dateFormats: Record<DateFormatType, DateFormatConfig> = {
    belgian: {
      short: 'dd/MM/yyyy',
      medium: 'dd MMM yyyy',
      long: 'EEEE, dd MMMM yyyy',
      locale: 'fr-BE',
      separator: '/',
      intlOptions: {
        short: { day: '2-digit', month: '2-digit', year: 'numeric' },
        medium: { day: '2-digit', month: 'short', year: 'numeric' },
        long: { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }
      }
    },
    european: {
      short: 'dd/MM/yyyy',
      medium: 'dd MMM yyyy',
      long: 'EEEE, dd MMMM yyyy',
      locale: 'en-GB',
      separator: '/',
      intlOptions: {
        short: { day: '2-digit', month: '2-digit', year: 'numeric' },
        medium: { day: '2-digit', month: 'short', year: 'numeric' },
        long: { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }
      }
    },
    us: {
      short: 'MM/dd/yyyy',
      medium: 'MMM dd, yyyy',
      long: 'EEEE, MMMM dd, yyyy',
      locale: 'en-US',
      separator: '/',
      intlOptions: {
        short: { month: '2-digit', day: '2-digit', year: 'numeric' },
        medium: { month: 'short', day: '2-digit', year: 'numeric' },
        long: { weekday: 'long', month: 'long', day: '2-digit', year: 'numeric' }
      }
    },
    iso: {
      short: 'yyyy-MM-dd',
      medium: 'yyyy-MM-dd',
      long: 'yyyy-MM-dd',
      locale: 'en-CA',
      separator: '-',
      intlOptions: {
        short: { year: 'numeric', month: '2-digit', day: '2-digit' },
        medium: { year: 'numeric', month: '2-digit', day: '2-digit' },
        long: { year: 'numeric', month: '2-digit', day: '2-digit' }
      }
    }
  };

  constructor(
    private userService: UserService,
    private authStore: AuthStore,
    private logger: LoggerService,
  ) {
    // Apply the authenticated user's saved date-format preference, reacting to
    // sign-in and profile changes. Reads straight from AuthStore — the single
    // source of truth for the current user — instead of the former dev-stub
    // AuthService (which always resolved a phantom `dev@example.com` user, so
    // this preference never actually loaded).
    effect(() => {
      const preferred = this.authStore.user()?.preferences?.dateFormat;
      if (preferred && preferred !== this.currentFormatSubject.value) {
        this.currentFormatSubject.next(preferred);
      }
    });
  }

  /**
   * Set the date format and optionally save to user preferences
   */
  async setDateFormat(format: DateFormatType, saveToProfile = true): Promise<void> {
    this.currentFormatSubject.next(format);

    if (!saveToProfile) return;

    const user = this.authStore.user();
    if (user?.userUID) {
      // `updateDoc` replaces the whole `preferences` map, so merge the existing
      // preferences forward — otherwise saving the date format would wipe the
      // user's theme/language/currency settings.
      this.userService
        .updateUser(user.userUID, { preferences: { ...user.preferences, dateFormat: format } })
        .pipe(take(1))
        .subscribe();
    }
  }

  /**
   * Get current date format configuration
   */
  getCurrentFormat(): DateFormatType {
    return this.currentFormatSubject.value;
  }

  /**
   * Get date format configuration for a specific format
   */
  getFormatConfig(format?: DateFormatType): DateFormatConfig {
    const formatType = format || this.getCurrentFormat();
    return this.dateFormats[formatType];
  }

  /**
   * Format a date using the current user preference. Accepts `unknown` because
   * callers regularly pass dynamic Firestore/template-variable payloads; shapes
   * that can't be coerced fall through to the empty-string path via `parseDate`.
   * See `DateLike` in `shared/utils/to-date.ts` for the recognised shapes.
   */
  formatDate(date: unknown, style: 'short' | 'medium' | 'long' = 'short'): string {
    if (!date) return '';

    const dateObj = this.parseDate(date);
    if (!dateObj || isNaN(dateObj.getTime())) return '';

    const config = this.getFormatConfig();
    return new Intl.DateTimeFormat(config.locale, config.intlOptions[style]).format(dateObj);
  }

  /**
   * Format a date with a specific format override
   */
  formatDateWithFormat(date: unknown, format: DateFormatType, style: 'short' | 'medium' | 'long' = 'short'): string {
    if (!date) return '';

    const dateObj = this.parseDate(date);
    if (!dateObj || isNaN(dateObj.getTime())) return '';

    const config = this.getFormatConfig(format);
    return new Intl.DateTimeFormat(config.locale, config.intlOptions[style]).format(dateObj);
  }

  /**
   * Format a date for input fields (always ISO format for HTML date inputs)
   */
  formatDateForInput(date: unknown): string {
    if (!date) return '';

    const dateObj = this.parseDate(date);
    if (!dateObj || isNaN(dateObj.getTime())) return '';

    return dateObj.toISOString().split('T')[0];
  }

  /**
   * Parse various date formats into a Date object
   */
  private parseDate(date: unknown): Date | null {
    if (!date) return null;

    if (date instanceof Date) {
      return date;
    }

    // Handle Firebase Timestamp objects (has toDate method)
    if (typeof date === 'object' && date !== null && 'toDate' in date && typeof (date as { toDate?: unknown }).toDate === 'function') {
      try {
        return (date as { toDate: () => Date }).toDate();
      } catch (e) {
        this.logger.warn('Failed to convert Timestamp to Date:', this.CONTEXT, e);
        return null;
      }
    }

    // Handle Firebase Timestamp objects (has seconds property)
    if (typeof date === 'object' && date !== null && 'seconds' in date) {
      try {
        const seconds = (date as { seconds: number }).seconds;
        return new Date(seconds * 1000);
      } catch (e) {
        this.logger.warn('Failed to convert timestamp with seconds to Date:', this.CONTEXT, e);
        return null;
      }
    }

    if (typeof date === 'string') {
      // Handle ISO timestamp strings
      if (date.includes('T')) {
        return new Date(date);
      }
      
      // Try smart parsing with user's format preference
      const smartParsed = this.parseUserFormatString(date);
      if (smartParsed) {
        return smartParsed;
      }
      
      // Fallback to native parsing
      return new Date(date);
    }
    
    // Handle numeric timestamps
    if (typeof date === 'number') {
      return new Date(date);
    }
    
    return null;
  }

  /**
   * Parse date string according to user's format preference.
   *
   * Strategy: try the user's preferred component order first, so genuinely
   * ambiguous inputs like 05/01/2005 are always read in the user's order.
   * Only if that fails do we fall back to the other orders, accepting a
   * fallback ONLY when it is unambiguous (exactly one order yields a valid
   * date). This keeps manual typing forgiving — a date typed in another common
   * layout, or before the async-loaded preference resolves, still maps to the
   * single date it can possibly mean — without ever silently rewriting a truly
   * ambiguous date.
   */
  parseUserFormatString(dateString: string): Date | null {
    if (!dateString || typeof dateString !== 'string') return null;

    const config = this.getFormatConfig();

    // Remove any extra whitespace and normalize separators to the user's.
    const normalizedInput = dateString.trim().replace(/\s+/g, '').replace(/[/\-.]/g, config.separator);

    const preferred = this.orderForLocale(config.locale);

    // 1. Preferred order wins outright — never reinterpret an ambiguous date.
    const preferredDate = this.parseWithOrder(normalizedInput, config.separator, preferred);
    if (preferredDate) return preferredDate;

    // 2. Fallback: accept another order only if exactly one distinct valid date exists.
    const distinct = new Map<number, Date>();
    for (const order of ['dmy', 'mdy', 'ymd'] as DateComponentOrder[]) {
      if (order === preferred) continue;
      const d = this.parseWithOrder(normalizedInput, config.separator, order);
      if (d) distinct.set(d.getTime(), d);
    }
    if (distinct.size === 1) {
      return distinct.values().next().value ?? null;
    }

    return null;
  }

  /**
   * Map a locale to the day/month/year order it writes dates in.
   */
  private orderForLocale(locale: string): DateComponentOrder {
    switch (locale) {
      case 'en-US': return 'mdy';
      case 'en-CA': return 'ymd';
      default: return 'dmy'; // Belgian, European
    }
  }

  /**
   * Extract and validate a date from a normalized string for a given component
   * order. Handles both separator-delimited ("22/06/2026") and 8-digit
   * continuous ("22062026") inputs. Returns null for anything invalid so the
   * caller can try another order.
   */
  private parseWithOrder(input: string, separator: string, order: DateComponentOrder): Date | null {
    let day: number, month: number, year: number;

    const parts = input.split(separator);
    if (parts.length === 3) {
      [day, month, year] = this.assignByOrder(parts[0], parts[1], parts[2], order);
    } else if (input.length === 8 && /^\d{8}$/.test(input)) {
      // Continuous: the 4-digit year's slice position depends on the order.
      if (order === 'ymd') {
        [year, month, day] = [+input.slice(0, 4), +input.slice(4, 6), +input.slice(6, 8)];
      } else if (order === 'mdy') {
        [month, day, year] = [+input.slice(0, 2), +input.slice(2, 4), +input.slice(4, 8)];
      } else {
        [day, month, year] = [+input.slice(0, 2), +input.slice(2, 4), +input.slice(4, 8)];
      }
    } else {
      return null;
    }

    return this.buildValidatedDate(day, month, year);
  }

  /**
   * Map three string components to [day, month, year] according to order.
   */
  private assignByOrder(a: string, b: string, c: string, order: DateComponentOrder): [number, number, number] {
    switch (order) {
      case 'mdy': return [parseInt(b, 10), parseInt(a, 10), parseInt(c, 10)];
      case 'ymd': return [parseInt(c, 10), parseInt(b, 10), parseInt(a, 10)];
      default: return [parseInt(a, 10), parseInt(b, 10), parseInt(c, 10)]; // dmy
    }
  }

  /**
   * Build a Date from numeric components with strict validation. Rejects
   * out-of-range components and JS auto-corrected dates (e.g. Feb 31 -> Mar 3).
   */
  private buildValidatedDate(day: number, month: number, year: number): Date | null {
    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    if (month < 1 || month > 12) return null;
    if (day < 1 || day > 31) return null;

    // Handle 2-digit years
    if (year < 100) {
      year += year < 50 ? 2000 : 1900;
    }

    const date = new Date(year, month - 1, day);

    // Reject JS auto-correction of impossible dates
    if (date.getDate() !== day || date.getMonth() !== month - 1 || date.getFullYear() !== year) {
      return null;
    }

    return this.isValidDate(date) ? date : null;
  }

  /**
   * Validate if the date is actually valid
   */
  private isValidDate(date: Date): boolean {
    return date instanceof Date && 
           !isNaN(date.getTime()) && 
           date.getFullYear() > 1900 && 
           date.getFullYear() < 2100;
  }

  /**
   * Get available date formats for user selection
   */
  getAvailableFormats(): {code: DateFormatType, name: string, example: string}[] {
    const sampleDate = new Date(2024, 11, 25); // December 25, 2024
    
    return [
      {
        code: 'belgian',
        name: 'Belgian (dd/mm/yyyy)',
        example: this.formatDateWithFormat(sampleDate, 'belgian', 'short')
      },
      {
        code: 'european',
        name: 'European (dd/mm/yyyy)',
        example: this.formatDateWithFormat(sampleDate, 'european', 'short')
      },
      {
        code: 'us',
        name: 'US (mm/dd/yyyy)',
        example: this.formatDateWithFormat(sampleDate, 'us', 'short')
      },
      {
        code: 'iso',
        name: 'ISO (yyyy-mm-dd)',
        example: this.formatDateWithFormat(sampleDate, 'iso', 'short')
      }
    ];
  }

  /**
   * Format date for display in tables and lists
   */
  formatDateForTable(date: unknown): string {
    return this.formatDate(date, 'short');
  }

  /**
   * Format date for display in forms and details
   */
  formatDateForDisplay(date: unknown): string {
    return this.formatDate(date, 'medium');
  }

  /**
   * Format date for headers and titles
   */
  formatDateForHeader(date: unknown): string {
    return this.formatDate(date, 'long');
  }
} 